// Evaluation, goal, and salary-band types — split from types.ts (MYB-44)

// ========== 인사평가 인터페이스 ==========

export interface EvaluationPeriod {
  id: number;
  company_id: string;
  name: string;
  period_type: string;
  year: number;
  half?: number;
  quarter?: number;
  start_date: string;
  end_date: string;
  status: string;
  description?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export const EVALUATION_AXES = ["technology", "product", "execution", "people", "business", "scalability"] as const;
export const AXIS_LABELS: Record<string, string> = {
  technology: "기술 경쟁력",
  product: "제품/서비스 역량",
  execution: "실행력",
  people: "조직·인재 역량",
  business: "비즈니스 역량",
  scalability: "확장·변화 대응력",
};

export interface EvaluationCriterion {
  id: number;
  company_id: string;
  code: string;
  axis: string;
  name: string;
  description?: string;
  weight: number;
  max_score: number;
  sort_order: number;
  is_manager_only: boolean;
  is_active: boolean;
}

export interface EvaluationAssignment {
  id: number;
  period_id: number;
  evaluatee_id: string;
  evaluatee_name?: string;
  evaluatee_team?: string;
  evaluator_id: string;
  evaluator_name?: string;
  evaluator_type: string;
  is_manager_evaluation: boolean;
  eval_profile?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface EvaluationScore {
  id: number;
  assignment_id: number;
  criteria_id: number;
  score: number;
  comment?: string;
  created_at: string;
}

export interface EvaluationResult {
  id: number;
  period_id: number;
  employee_id: string;
  axis_scores?: Record<string, number | null>;
  competency_score?: number;
  leadership_score?: number;
  achievement_score?: number;
  total_score?: number;
  grade?: string;
  is_manager: boolean;
  eval_profile?: string;
  final_comment?: string;
  confirmed_by?: string;
  confirmed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyValue {
  id: number;
  keyword: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

export interface EvaluationInterview {
  id: number;
  result_id: number;
  interviewer_id: string;
  interviewee_id: string;
  interview_date: string;
  interview_type?: string;
  content?: string;
  goals?: string;
  agreed_actions?: string;
  interviewee_signature: boolean;
  status?: 'scheduled' | 'completed' | 'signed';
  structured_content?: {
    strengths?: string;
    weaknesses?: string;
    improvement_plan?: string;
    action_items?: string;
    next_steps?: string;
    overall_feedback?: string;
  };
  scheduled_by?: string;
  created_at: string;
}

export interface InterviewStats {
  total_employees: number;
  interviews_scheduled: number;
  interviews_completed: number;
  interviews_signed: number;
  not_scheduled: { employee_id: string; employee_name: string }[];
  completion_rate: number;
}

export interface EvaluationAppeal {
  id: number;
  result_id: number;
  appellant_id: string;
  appeal_reason: string;
  appeal_date: string;
  status: string;
  reviewer_id?: string;
  review_comment?: string;
  review_date?: string;
  original_grade?: string;
  revised_grade?: string;
  created_at: string;
}

export interface EvaluationNotification {
  id: number;
  result_id: number;
  recipient_id: string;
  notification_type?: string;
  message?: string;
  sent_at?: string;
  acknowledged_at?: string;
  status: string;
  created_at: string;
}

export interface EvaluationRadarData {
  employee_id: string;
  axes: { axis: string; score: number; max_score: number }[];
  period_id?: number;
}

export interface NotificationCompliance {
  period_id: number;
  total_employees: number;
  notified: number;
  acknowledged: number;
  overdue: number;
  compliance_rate: number;
  deadline_days: number;
  details: { employee_id: string; notified_at?: number; acknowledged_at?: number; is_overdue: boolean; days_since_result: number }[];
}

export interface AppealDeadlineStatus {
  period_id: number;
  total_appeals: number;
  within_deadline: number;
  overdue_submit: number;
  overdue_review: number;
  details: { appeal_id: number; employee_id: string; appeal_date: string; status: string; submit_deadline: string; review_deadline: string; is_submit_overdue: boolean; is_review_overdue: boolean }[];
}

export interface BiasAnalysis {
  period_id: number;
  total_evaluated: number;
  gender_distribution: Record<string, Record<string, number>>;
  age_distribution: Record<string, Record<string, number>>;
  warnings: { type: string; description: string; threshold: number; actual: number }[];
}

export interface GradeCalibration {
  id: number;
  result_id: number;
  employee_id?: string;
  original_grade: string;
  adjusted_grade: string;
  reason: string;
  calibrated_by: string;
  calibrated_at: number;
}

export interface DistributionSuggestion {
  employee_id: string;
  current_grade: string;
  suggested_grade: string;
  total_score: number;
  distance_to_boundary: number;
  direction: 'upgrade' | 'downgrade';
}

export interface DistributionSuggestionsResponse {
  valid: boolean;
  total: number;
  violations: { grade: string; target_pct: number; actual_pct: number; actual_count: number; diff: number }[];
  suggestions: DistributionSuggestion[];
}

export interface RadarAxis {
  name: string;
  value: number;
  max_value: number;
}

export interface MultiRadarData {
  entity_id: string;
  period_id: number;
  perspective: 'hr' | 'org' | 'leader';
  axes: RadarAxis[];
  org_name?: string;
  member_count?: number;
}

// ========== MBO/KPI 목표 관리 ==========
export interface Goal {
  id: number;
  period_id: number;
  employee_id: string;
  employee_name?: string;
  team?: string;
  parent_goal_id?: number;
  org_unit_id?: number;
  goal_type: string;
  title: string;
  description?: string;
  metric_name?: string;
  metric_unit?: string;
  target_value?: number;
  weight: number;
  start_date?: string;
  due_date?: string;
  status: string;
  achievement_rate: number;
  final_score?: number;
  created_by?: string;
  approved_by?: string;
  approved_at?: number;
  created_at: number;
  updated_at: number;
  children?: Goal[];
  company_id?: number;
  tier?: number;
  child_count?: number;
  rollup_rate?: number;
}

export interface GoalProgress {
  id: number;
  goal_id: number;
  record_date: string;
  actual_value?: number;
  achievement_rate?: number;
  description?: string;
  evidence?: string;
  recorded_by: string;
  created_at: number;
}

export interface GoalReview {
  id: number;
  goal_id: number;
  reviewer_id: string;
  review_date: string;
  review_type: string;
  content: string;
  revised_target_value?: number;
  revised_due_date?: string;
  recommendation?: string;
  created_at: number;
}

export interface GroupRankLevel {
  id: number;
  code: string;
  name: string;
  level: number;
  description?: string;
}

export interface RankLevelMapping {
  id: number;
  company_id: number;
  rank_id: number;
  rank_name?: string;
  rank_level?: number;
  group_level_id: number;
  group_level_code?: string;
  group_level_name?: string;
  group_level?: number;
}

export interface SalaryBand {
  id: number;
  company_id: number;
  group_level_id: number;
  group_level_code?: string;
  group_level_name?: string;
  group_level?: number;
  min_salary: number;
  max_salary: number;
  base_salary: number;
}

export interface EmployeeAchievement {
  employee_id: string;
  period_id: number;
  total_goals: number;
  weighted_achievement: number;
  achv_score: number;
  by_type: Record<string, number>;
}

export interface AchievementSummary {
  period_id: number;
  total_employees: number;
  avg_achievement: number;
  employees: {
    employee_id: string;
    employee_name: string;
    team: string;
    goal_count: number;
    weighted_achievement: number;
    achv_score: number;
  }[];
}
